import { AvoirAddon } from './tabs/AvoirAddon.js';
import { SouscriptionAddon } from './tabs/SouscriptionAddon.js';
import { TableauDefilant } from '../components/TableauDefilant.js';
import tableStyles from '../styles/table.module.css';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Input, Select, Tabs } from '@artosera/ui';
import { centimesDepuisEuros } from '@artosera/shared';
import { ApiError, modeDemo } from '../lib/api.js';
import { ventesAddonsApi, type FactureAddonDetail } from '../lib/addon-ventes.js';
import { usePeut } from '../auth/usePeut.js';
import { estElevationAnnulee, useElevation } from '../components/useElevation.js';
import styles from '../styles/page.module.css';
import form from '../styles/form.module.css';
export function VentesAddons() {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const [params, setParams] = useSearchParams();
  const onglet = params.get('onglet') ?? 'commandes';
  const [factureId, setFactureId] = useState<string | null>(null);
  const ventes = useQuery({
    queryKey: ['console', 'addon-ventes', params.toString()],
    queryFn: () => ventesAddonsApi.ventes(params),
    enabled: peut('console:read-addon-sales') && !modeDemo(),
  });
  const prix = (n: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(n / 100);
  const changer = (cle: string, valeur: string) => {
    const suite = new URLSearchParams(params);
    suite.set(cle, valeur);
    if (cle !== 'page') suite.set('page', '1');
    setParams(suite);
  };
  const tableau = (
    <>
      {ventes.data ? (
        <TableauDefilant>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th>{t('ventes.designation')}</th>
                <th>{t('ventes.galerie')}</th>
                <th>
                  {t(
                    ['factures', 'impayes_30', 'a_regulariser'].includes(onglet)
                      ? 'ventes.ttc'
                      : 'ventes.ht',
                  )}
                </th>
                <th>{t('ventes.echeance')}</th>
                <th>{t('ventes.etat')}</th>
              </tr>
            </thead>
            <tbody>
              {ventes.data.items.map((ligne) => (
                <tr key={ligne.id}>
                  <td>
                    {ligne.numero ? (
                      <Button onClick={() => setFactureId(ligne.factureId ?? ligne.id)}>
                        {ligne.numero}
                      </Button>
                    ) : (
                      t(`commun:modules.${ligne.addon}.libelle`)
                    )}
                    {ligne.estTest ? ` · ${t('ventes.specimen')}` : ''}
                  </td>
                  <td>{ligne.galerieNom ?? t('ventes.galerie')}</td>
                  <td>{prix(ligne.ttcCentimes ?? ligne.htNetCentimes ?? 0)}</td>
                  <td>{ligne.echeance ?? ligne.finPeriode ?? ligne.jusquAu ?? '—'}</td>
                  <td>
                    {t(`ventes.${ligne.etat ?? ligne.nature ?? ligne.formule ?? 'active'}`)}
                    {(onglet === 'souscriptions' || onglet === 'a_traiter') &&
                    ligne.etat &&
                    ligne.finPeriode &&
                    ligne.revision !== undefined ? (
                      <SouscriptionAddon
                        galerieId={ligne.galerieId}
                        souscription={{
                          id: ligne.id,
                          etat: ligne.etat,
                          finPeriode: ligne.finPeriode,
                          revision: ligne.revision,
                          raisonSuspension: ligne.raisonSuspension,
                        }}
                      />
                    ) : null}
                    {ligne.factureId && !ligne.numero ? (
                      <Button onClick={() => setFactureId(ligne.factureId!)}>
                        {t('ventes.facture')}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableauDefilant>
      ) : null}
    </>
  );
  if (!peut('console:read-addon-sales')) return null;
  return (
    <section>
      <header className={styles.entete}>
        <h1>{t('ventes.titre')}</h1>
      </header>
      {ventes.isPending && !modeDemo() ? <p role="status">{t('ventes.chargement')}</p> : null}
      {ventes.isError || modeDemo() ? <p role="alert">{t('ventes.erreur')}</p> : null}
      {ventes.data ? (
        <div className={styles.panneau}>
          <dl className={styles.grilleStats}>
            <div className={styles.stat}>
              <dt>{t('ventes.recurrent')}</dt>
              <dd>{prix(ventes.data.indicateurs.revenuMensuelHtCentimes)}</dd>
            </div>
            <div className={styles.stat}>
              <dt>{t('ventes.essais')}</dt>
              <dd>{ventes.data.indicateurs.essaisEnCours}</dd>
            </div>
            <div className={styles.stat}>
              <dt>{t('ventes.conversion')}</dt>
              <dd>
                {ventes.data.indicateurs.essaisConvertis} / {ventes.data.indicateurs.essaisClos} ·{' '}
                {ventes.data.indicateurs.tauxConversionPointsDeBase / 100} %
              </dd>
            </div>
            <div className={styles.stat}>
              <dt>{t('ventes.retard')}</dt>
              <dd>
                {ventes.data.indicateurs.facturesEnRetard} ·{' '}
                {prix(ventes.data.indicateurs.retardTtcCentimes)}
              </dd>
            </div>
            <div className={styles.stat}>
              <dt>{t('ventes.tests')}</dt>
              <dd>{ventes.data.espacesTest}</dd>
            </div>
          </dl>
        </div>
      ) : null}
      <label>
        {t('ventes.tests')}
        <Select
          disabled={onglet === 'impayes_30'}
          value={onglet === 'impayes_30' ? 'false' : (params.get('test') ?? 'false')}
          onValueChange={(v) => changer('test', v)}
          options={[
            { value: 'false', label: t('ventes.exclus') },
            { value: 'true', label: t('ventes.seulementTests') },
            { value: 'tous', label: t('ventes.tous') },
          ]}
        />
      </label>
      {onglet === 'factures' ? (
        <label>
          {t('ventes.etat')}
          <Select
            value={params.get('etat') ?? 'tous'}
            onValueChange={(v) => {
              const suite = new URLSearchParams(params);
              if (v === 'tous') suite.delete('etat');
              else suite.set('etat', v);
              suite.set('page', '1');
              setParams(suite);
            }}
            options={['tous', 'a_payer', 'en_retard', 'reglee'].map((etat) => ({
              value: etat,
              label: t(`ventes.${etat}`),
            }))}
          />
        </label>
      ) : null}
      <Tabs
        value={onglet}
        onValueChange={(v) => changer('onglet', v)}
        tabs={[
          'commandes',
          'souscriptions',
          'factures',
          'a_traiter',
          'impayes_30',
          'a_regulariser',
        ].map((v) => ({
          value: v,
          label: t(`ventes.${v}`),
          content: tableau,
        }))}
      />
      {ventes.data ? (
        <nav aria-label={t('ventes.pages')}>
          <Button
            disabled={ventes.data.page === 1}
            onClick={() => changer('page', String(ventes.data!.page - 1))}
          >
            {t('ventes.precedent')}
          </Button>{' '}
          {ventes.data.page}{' '}
          <Button
            disabled={ventes.data.page * 50 >= ventes.data.total}
            onClick={() => changer('page', String(ventes.data!.page + 1))}
          >
            {t('ventes.suivant')}
          </Button>
        </nav>
      ) : null}
      {factureId ? (
        <DetailFacture key={factureId} id={factureId} fermer={() => setFactureId(null)} />
      ) : null}
    </section>
  );
}
function DetailFacture({ id, fermer }: { id: string; fermer: () => void }) {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const elevation = useElevation();
  const requete = useQuery({
    queryKey: ['console', 'addon-facture', id],
    queryFn: () => ventesAddonsApi.facture(id),
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [operation, setOperation] = useState('encaisser');
  const facture = requete.data;
  let titreGeste = t('ventes.annuler');
  if (operation === 'encaisser') titreGeste = t('ventes.saisir');
  if (operation === 'renvoyer') titreGeste = t('ventes.renvoyer');
  if (operation === 'rembourser-en-ligne') titreGeste = t('ventes.rembourserEnLigne');
  if (operation === 'rembourser') titreGeste = t('ventes.rembourser');
  const ligneAnnulee = facture?.encaissements.find((e) => e.id === operation);
  const mutation = useMutation({
    retry: false,
    mutationFn: async (data: FormData) => {
      setErreur(null);
      const fraiche = await ventesAddonsApi.facture(id);
      if (!facture || fraiche.revision !== facture.revision || fraiche.envoi !== facture.envoi)
        throw new ApiError(409, 'conflit.version', t('ventes.actualiser'), {
          raison: 'revision_facture_perimee',
        });
      if (operation === 'encaisser')
        return ventesAddonsApi.encaisser(id, {
          encaisseLe: String(data.get('date')),
          montantTtcCentimes: centimesDepuisEuros(String(data.get('montant'))),
          referenceBancaire: String(data.get('reference')).trim(),
          revisionFacture: fraiche.revision,
        });
      if (operation === 'renvoyer') return ventesAddonsApi.renvoyer(id, String(data.get('motif')));
      if (operation === 'rembourser-en-ligne')
        return elevation.executer(() =>
          ventesAddonsApi.rembourserEnLigne(id, {
            paiementId: String(data.get('paiementId')),
            montantTtcCentimes: centimesDepuisEuros(String(data.get('montant'))),
            motif: String(data.get('motif')).trim(),
            revisionFacture: fraiche.revision,
          }),
        );
      if (operation === 'rembourser')
        return elevation.executer(() =>
          ventesAddonsApi.rembourser(id, {
            rembourseLe: String(data.get('date')),
            montantTtcCentimes: centimesDepuisEuros(String(data.get('montant'))),
            referenceBancaire: String(data.get('reference')).trim(),
            motif: String(data.get('motif')).trim(),
            revisionFacture: fraiche.revision,
          }),
        );
      return elevation.executer(() =>
        ventesAddonsApi.annuler(id, operation, String(data.get('motif'))),
      );
    },
    onError: (e: unknown) => {
      if (estElevationAnnulee(e)) return;
      if (e instanceof ApiError) {
        const raison = (e.details as { raison?: string } | null)?.raison;
        setErreur(t(`ventes.erreurs.${raison}`, { defaultValue: t('ventes.erreur') }));
      } else if (e instanceof RangeError) setErreur(t('ventes.montantInvalide'));
      else setErreur(t('ventes.erreur'));
    },
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['console', 'addon-facture', id] }),
        client.invalidateQueries({ queryKey: ['console', 'addon-ventes'] }),
        client.invalidateQueries({ queryKey: ['console', 'comptabilite'] }),
      ]);
    },
  });
  const prix = (n: number) =>
    new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(n / 100);
  return (
    <section className={styles.panneau} aria-label={t('ventes.facture')}>
      <Button onClick={fermer}>{t('ventes.fermer')}</Button>
      {requete.isError ? <p role="alert">{t('ventes.erreur')}</p> : null}
      {facture ? (
        <>
          <h2>
            {facture.numero}
            {facture.estTest ? ` · ${t('ventes.specimen')}` : ''}
          </h2>
          <p>
            {facture.destinataire.nom} · {t(`ventes.${facture.etat}`)} · {facture.echeance}
          </p>
          <TableauDefilant>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <th>{t('ventes.designation')}</th>
                  <th>{t('ventes.ht')}</th>
                </tr>
              </thead>
              <tbody>
                {facture.lignes.map((ligne, i) => (
                  <tr key={i}>
                    <td>
                      {ligne.designation} × {ligne.quantite}
                    </td>
                    <td>{prix(ligne.prixUnitaireHtCentimes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableauDefilant>
          <p>
            {t('ventes.ht')} : {prix(facture.totaux.totalLignesCentimes)} · {t('ventes.tva')} :{' '}
            {prix(facture.totaux.totalTvaCentimes)} ({t(`ventes.${facture.tva.regime}`)}) ·{' '}
            {t('ventes.ttc')} : {prix(facture.ttcCentimes)} · {t('ventes.regle')} :{' '}
            {prix(facture.regleCentimes)}
          </p>
          <p>
            {t('ventes.avoirs')} : {prix(facture.avoirsTtcCentimes)} · {t('ventes.resteDu')} :{' '}
            {prix(facture.resteDuTtcCentimes)}
          </p>
          {facture.aRembourserTtcCentimes > 0 ? (
            <p role="status">
              {t('ventes.aRembourser', { montant: prix(facture.aRembourserTtcCentimes) })}
            </p>
          ) : null}
          {facture.remboursementsEnLigne.length ? (
            <ul aria-label={t('ventes.remboursementsEnLigne')}>
              {facture.remboursementsEnLigne.map((r) => (
                <li key={r._id}>
                  {prix(r.montantTtcCentimes)} · {r.paiementId} ·{' '}
                  {t(`ventes.remboursement_${r.statut}`)}
                </li>
              ))}
            </ul>
          ) : null}
          {facture.remboursements?.length ? (
            <>
              <h3>{t('ventes.remboursements')}</h3>
              <ul>
                {facture.remboursements.map((r, i) => (
                  <li key={i}>
                    {r.rembourseLe} · {prix(r.montantTtcCentimes)} · {r.referenceBancaire} ·{' '}
                    {r.motif} · {r.auteurEmail}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          <ul>
            {facture.avoirs.map((a) => (
              <li key={a.id}>
                {a.pdfDisponible ? (
                  <a href={`/v1/console/addons/factures/${a.id}/pdf`} download>
                    {a.numero} · {prix(a.ttcCentimes)}
                  </a>
                ) : (
                  `${a.numero} · ${prix(a.ttcCentimes)} · ${t('ventes.preparation')}`
                )}
              </li>
            ))}
          </ul>
          {peut('console:manage-addon-billing') && facture.type === 'facture' ? (
            <AvoirAddon facture={facture} />
          ) : null}
          <p>
            {t('ventes.envoi')} : {t(`ventes.${facture.envoi}`)}
            {facture.creance
              ? ` · ${t('ventes.creance')} : ${t(`ventes.${facture.creance.etat}`)}`
              : ''}
          </p>
          {facture.pdfDisponible ? (
            <a href={`/v1/console/addons/factures/${id}/pdf`} download>
              {t('ventes.telecharger')}
            </a>
          ) : (
            <p>{t('ventes.preparation')}</p>
          )}
          <ul>
            {facture.encaissements.map((e) => (
              <li key={e.id}>
                {e.encaisseLe} · {prix(e.montantTtcCentimes)} · {e.referenceBancaire}
                {peut('console:manage-addon-billing') ? (
                  <Button
                    disabled={mutation.isPending}
                    onClick={() => {
                      setErreur(null);
                      setOperation(e.id);
                    }}
                  >
                    {t('ventes.annuler')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          {peut('console:manage-addon-billing') ? (
            <>
              <Button
                disabled={mutation.isPending || facture.resteDuTtcCentimes === 0}
                onClick={() => setOperation('encaisser')}
              >
                {t('ventes.saisir')}
              </Button>
              {facture.aRembourserTtcCentimes > 0 && facture.paiementsEnLigne.length ? (
                <Button
                  disabled={mutation.isPending}
                  onClick={() => setOperation('rembourser-en-ligne')}
                >
                  {t('ventes.rembourserEnLigne')}
                </Button>
              ) : null}
              {facture.type === 'facture' && facture.aRembourserTtcCentimes > 0 ? (
                <Button disabled={mutation.isPending} onClick={() => setOperation('rembourser')}>
                  {t('ventes.rembourser')}
                </Button>
              ) : null}
              <Button
                disabled={
                  mutation.isPending || !facture.pdfDisponible || facture.envoi === 'a_envoyer'
                }
                onClick={() => setOperation('renvoyer')}
              >
                {t('ventes.renvoyer')}
              </Button>
              <h3>{titreGeste}</h3>
              {ligneAnnulee ? (
                <p>
                  {ligneAnnulee.encaisseLe} · {prix(ligneAnnulee.montantTtcCentimes)} ·{' '}
                  {ligneAnnulee.referenceBancaire}
                </p>
              ) : null}
              <form
                key={`${id}-${operation}-${facture.revision}-${facture.envoi}`}
                className={form.champ}
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!mutation.isPending) mutation.mutate(new FormData(e.currentTarget));
                }}
              >
                <fieldset
                  disabled={
                    mutation.isPending ||
                    (['rembourser', 'rembourser-en-ligne'].includes(operation) &&
                      facture.aRembourserTtcCentimes === 0) ||
                    (operation === 'encaisser' && facture.resteDuTtcCentimes === 0)
                  }
                >
                  {operation === 'rembourser-en-ligne' ? (
                    <>
                      <label>
                        {t('ventes.paiementEnLigne')}
                        <select name="paiementId" required>
                          {facture.paiementsEnLigne.map((p) => (
                            <option key={p.paiementId} value={p.paiementId}>
                              {p.paiementId} · {prix(p.montantCentimes)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label>
                        {t('ventes.montant')}
                        <Input name="montant" inputMode="decimal" required />
                      </label>
                      <p>{t('ventes.remboursementTest')}</p>
                    </>
                  ) : null}
                  {operation === 'encaisser' || operation === 'rembourser' ? (
                    <ChampsEncaissement
                      facture={facture}
                      remboursement={operation === 'rembourser'}
                    />
                  ) : null}
                  {operation !== 'encaisser' ? (
                    <label>
                      {t('ventes.motif')}
                      <Input name="motif" required maxLength={500} />
                    </label>
                  ) : null}
                  <Button type="submit">
                    {operation === 'encaisser' ? t('ventes.encaisser') : t('ventes.confirmer')}
                  </Button>
                </fieldset>
              </form>
              {erreur ? <p role="alert">{erreur}</p> : null}
            </>
          ) : null}
        </>
      ) : null}
      {elevation.dialogue}
    </section>
  );
}
function ChampsEncaissement({
  facture,
  remboursement = false,
}: {
  facture: FactureAddonDetail;
  remboursement?: boolean;
}) {
  const { t } = useTranslation('console');
  return (
    <>
      <label>
        {t(remboursement ? 'ventes.dateRemboursement' : 'ventes.date')}
        <Input name="date" type="date" min={facture.dateEmission} required />
      </label>
      <label>
        {t('ventes.montantTtc')}
        <Input name="montant" inputMode="decimal" required pattern="[0-9]+([.,][0-9]{1,2})?" />
      </label>
      <label>
        {t('ventes.reference')}
        <Input name="reference" required maxLength={140} />
      </label>
    </>
  );
}
